import { User } from 'lib-shared-types-api';
import { useCallback } from 'react';

import { appActions } from 'app/app.actions';
import { bindRtkActionToPromise } from 'app/utils/promiseActions';
import { AppState } from 'bootstrap.store';
import { userModalActions } from 'components/usersModal';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppSelector } from 'utils/redux/useAppSelector';
import { bindActionToPromise } from 'utils/sagaApiGeneratorHelpers';
import { useFavoriteAgents } from 'utils/useFavoriteAgents';

import { twilioActions } from '../../../app/middleware/twilio/actions/twilioActions';
import { websocketActions } from '../../../app/middleware/websocket/actions/websocket';
import { presenceActions } from '../../presence/actions/presenceActions';
import { teamActions } from '../actions/teamPage';
import { TeamList as TeamListImpl } from '../components/teamList';

const { teamToggleAgentDetails } = teamActions;
const { presenceGet } = presenceActions;

export type TeamListStateProps = {
  profile: AppState['settings']['profile']['profile'];
  appPresence: {
    asyncStatus: AppState['presence']['meta']['getAsyncStatus'];
    items: AppState['presence']['allPresence'];
    item: AppState['presence']['presence'];
  };
  agentsDetail: AppState['team']['agentsDetail'];
  favouriteTeamMemberIds: string[];
  isMicrophoneBlocked: boolean;
};

export type TeamListDispatchProps = {
  wsRequest: (payload: any) => Promise<any>;
  twilioMakeInternalCall: (payload: any) => Promise<any>;
  personalAddToFavouriteTeamMemberIds: (payload: {
    agentId: string;
  }) => Promise<any>;
  personalRemoveFromFavouriteTeamMemberIds: (payload: {
    agentId: string;
  }) => Promise<any>;
  showUserModal: (user: Partial<User>) => void;
  transferToQueueModalOpen: (csid: number) => void;
  toggleAgentDetails: (id: string) => void;
  getPresence: (payload: any) => Promise<any>;
};

type ExternalProps = {
  presence: any;
};

const TeamList = (props: ExternalProps): JSX.Element => {
  const dispatch = useAppDispatch();
  const { favoriteAgentIds, addFavorite, removeFavorite } = useFavoriteAgents();

  const profile = useAppSelector((state) => state.settings.profile.profile);
  const appPresence = useAppSelector((state) => ({
    asyncStatus: state.presence.meta.getAsyncStatus,
    items: state.presence.allPresence,
    item: state.presence.presence,
  }));
  const agentsDetail = useAppSelector((state) => state.team.agentsDetail);
  const isMicrophoneBlocked = useAppSelector(
    (state) => state.telephony.isMicrophoneBlocked,
  );

  const wsRequest = useCallback(
    (payload: any) =>
      bindRtkActionToPromise(dispatch, websocketActions.request)(payload),
    [dispatch],
  );

  const twilioMakeInternalCall = useCallback(
    (payload: any) =>
      bindActionToPromise(
        dispatch,
        twilioActions.twilioMakeInternalCall,
      )(payload),
    [dispatch],
  );

  const personalAddToFavouriteTeamMemberIds = useCallback(
    (payload: { agentId: string }) => {
      addFavorite(payload.agentId);
      return Promise.resolve();
    },
    [addFavorite],
  );

  const personalRemoveFromFavouriteTeamMemberIds = useCallback(
    (payload: { agentId: string }) => {
      removeFavorite(payload.agentId);
      return Promise.resolve();
    },
    [removeFavorite],
  );

  const showUserModal = useCallback(
    (user: Partial<User>) => dispatch(userModalActions.show({ user })),
    [dispatch],
  );

  const transferToQueueModalOpenFn = useCallback(
    (csid: number) =>
      dispatch(
        appActions.showModal({
          modal: {
            type: 'app:conversationQueueTransfer',
            arg: {
              csid,
              isWarmTransferAvailable: false,
            },
          },
        }),
      ),
    [dispatch],
  );

  const toggleAgentDetails = useCallback(
    (id: string) => dispatch(teamToggleAgentDetails({ id })),
    [dispatch],
  );

  const getPresence = useCallback(
    (payload: any) => bindActionToPromise(dispatch, presenceGet)(payload),
    [dispatch],
  );

  return (
    <TeamListImpl
      {...props}
      profile={profile}
      appPresence={appPresence}
      agentsDetail={agentsDetail}
      favouriteTeamMemberIds={favoriteAgentIds as string[]}
      isMicrophoneBlocked={isMicrophoneBlocked}
      wsRequest={wsRequest}
      twilioMakeInternalCall={twilioMakeInternalCall}
      personalAddToFavouriteTeamMemberIds={personalAddToFavouriteTeamMemberIds}
      personalRemoveFromFavouriteTeamMemberIds={
        personalRemoveFromFavouriteTeamMemberIds
      }
      showUserModal={showUserModal}
      transferToQueueModalOpen={transferToQueueModalOpenFn}
      toggleAgentDetails={toggleAgentDetails}
      getPresence={getPresence}
    />
  );
};

export { TeamList };
